import React, { useState } from 'react';
import { addDoc, collection, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getPFIContactInfo } from '../lib/pfiContactSettings';
import { 
  XMarkIcon, 
  PaperAirplaneIcon,
  DocumentTextIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';

const JobSheetModal = ({ 
  task, 
  project, 
  suppliers, 
  isOpen, 
  onClose, 
  onJobSheetSent 
}) => {
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleSendJobSheet = async () => {
    if (!selectedSupplier || !task || !project) return;

    setIsGenerating(true);
    try {
      const supplier = suppliers.find(s => s.id === selectedSupplier);
      if (!supplier) {
        throw new Error('Supplier not found');
      }

      // Generate unique reference number
      const referenceNumber = `JS-${Date.now().toString().slice(-6)}-${Math.random().toString(36).substring(2, 5).toUpperCase()}`;
      
      // Get PFI contact information
      const pfiContact = await getPFIContactInfo();
      
      // Create job sheet with comprehensive project data
      const jobSheetData = {
        taskId: task.id,
        projectId: project.id,
        supplierId: supplier.id,
        supplierName: supplier.name,
        supplierEmail: supplier.email,
        supplierPhone: supplier.phone || '',
        referenceNumber,
        taskDetails: {
          name: task.name,
          description: task.description || '',
          category: task.category || 'general',
          budget: task.budget || null,
          timeline: task.estimatedDays ? `${task.estimatedDays} days` : '',
          specifications: additionalNotes,
          notes: task.notes || ''
        },
        projectDetails: {
          name: project.name,
          description: project.description || '',
          address: project.address || '',
          customerInfo: project.customerName || '',
          startDate: project.startDate || null,
          endDate: project.endDate || null,
          photos: project.photos || [],
          customerContact: project.customerContact || null
        },
        contactInfo: {
          primaryContact: pfiContact.primaryContact,
          phone: pfiContact.phone,
          email: pfiContact.projectsEmail || pfiContact.email,
          address: pfiContact.address,
          companyName: pfiContact.companyName
        },
        sentAt: new Date(),
        acknowledgmentDeadline: new Date(Date.now() + 48 * 60 * 60 * 1000), // 48 hours
        status: 'sent',
        queries: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Save job sheet to database
      const jobSheetRef = await addDoc(collection(db, 'jobSheets'), jobSheetData);
      
      // Update task status and add job sheet reference
      await updateDoc(doc(db, 'projects', project.id, 'tasks', task.id), {
        status: 'job_sheet_sent',
        jobSheetId: jobSheetRef.id,
        jobSheetSentAt: new Date(),
        assignedSupplierId: supplier.id,
        assignedSupplierName: supplier.name,
        assignedSupplierEmail: supplier.email,
        updatedAt: new Date()
      });

      // Create notification for supplier
      await addDoc(collection(db, 'notifications'), {
        type: 'job-sheet-sent',
        recipientId: supplier.id,
        recipientEmail: supplier.email,
        recipientPhone: supplier.phone || null,
        recipientName: supplier.name,
        projectId: project.id,
        projectName: project.name,
        taskId: task.id,
        taskName: task.name,
        taskDescription: task.description || '',
        referenceNumber: referenceNumber,
        message: `Job sheet sent: ${task.name}`,
        acknowledgmentRequired: true,
        acknowledgmentDeadline: new Date(Date.now() + 48 * 60 * 60 * 1000),
        jobSheetUrl: `${window.location.origin}/job-sheet/${jobSheetRef.id}`,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      alert(`✅ Job sheet sent successfully!\n\nReference: ${referenceNumber}\nSupplier: ${supplier.name}\n\nThe supplier has been notified and has 48 hours to acknowledge receipt.`);
      
      if (onJobSheetSent) {
        onJobSheetSent();
      }
      onClose();
      
    } catch (error) {
      console.error('Error sending job sheet:', error);
      alert('Failed to send job sheet: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setSelectedSupplier('');
    setAdditionalNotes('');
    onClose();
  };

  if (!isOpen || !task) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <DocumentTextIcon className="w-8 h-8 text-pink-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Generate Job Sheet</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Task Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Task Details</h3>
              <p className="text-gray-700 font-medium">{task.name}</p>
              {task.description && (
                <p className="text-gray-600 text-sm mt-1">{task.description}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span className="capitalize">{task.category}</span>
                {task.estimatedDays && <span>{task.estimatedDays} days estimated</span>}
              </div>
            </div>

            {/* Supplier Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Supplier *
              </label>
              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                required
              >
                <option value="">Choose a supplier...</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name} - {supplier.category}
                  </option>
                ))}
              </select>
            </div>

            {/* Additional Notes/Specifications */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes & Specifications
              </label>
              <textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Any specific requirements, materials, access instructions, or additional details for the supplier..."
              />
            </div>

            {/* Job Sheet Preview */}
            {selectedSupplier && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  Job Sheet Summary
                </h3>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>Supplier:</strong> {suppliers.find(s => s.id === selectedSupplier)?.name}</p>
                  <p><strong>Task:</strong> {task.name}</p>
                  <p><strong>Project:</strong> {project.name}</p>
                  <p><strong>Acknowledgment Required:</strong> Within 48 hours</p>
                  <p><strong>Status Tracking:</strong> Supplier can submit queries after acknowledgment</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center space-x-4 pt-4">
              <button
                onClick={handleSendJobSheet}
                disabled={!selectedSupplier || isGenerating}
                className={`flex-1 flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                  selectedSupplier && !isGenerating
                    ? 'bg-pink-600 text-white hover:bg-pink-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                    Send Job Sheet
                  </>
                )}
              </button>
              <button
                onClick={handleClose}
                disabled={isGenerating}
                className="px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSheetModal;